// src/pages/HandoverFormPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useCountry } from '../context/CountryContext';

import {
  GetReservation,
  ListCars,
  HandoverByReservation,
} from '../handover/handoverQueries';

import { COLORS } from '../handover/handoverStyles';

import LockedHandoverNotice from '../components/handover/LockedHandoverNotice';
import HandoverPdfDownloadButton, {
  canDownloadHandoverPdf,
} from '../components/handover/HandoverPdfDownloadButton';
import PickupHandoverForm from '../handover/PickupHandoverForm';
import DropoffHandoverForm from '../handover/DropoffHandoverForm';
import SubstitutionHandoverForm from '../handover/SubstitutionHandoverForm';

const client = createAuditedClient('HandoverFormPage');

function safeParseJson(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(String(value));
  } catch {
    return {};
  }
}

export default function HandoverFormPage() {
  const { reservationId, type } = useParams();
  const navigate = useNavigate();
  const locationHook = useLocation();
  const mode =
    type === 'dropoff' ? 'dropoff' : type === 'substitution' ? 'substitution' : 'pickup';

  const { country } = useCountry(); // 'GE' | 'AL' etc.
  const location = country === 'Albania' ? 'AL' : 'GE';

  const [reservation, setReservation] = useState(null);
  const [cars, setCars] = useState([]);
  const [claims, setClaims] = useState(null);
  const [pickupFuelLevel, setPickupFuelLevel] = useState(null);
  const [pickupHandover, setPickupHandover] = useState(null);
  const [dropoffHandover, setDropoffHandover] = useState(null);
  const [substitutionHandovers, setSubstitutionHandovers] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔔 Toast alert state
  const [alerts, setAlerts] = useState([]);

  const searchParams = new URLSearchParams(locationHook.search);
  const readonlyFromQuery = searchParams.get('readonly') === '1';
  const forceEdit = searchParams.get('forceEdit') === '1';
  const isAdmin = (claims?.groups || []).includes('admin');
  const canBypassLock = isAdmin && forceEdit;
  const showPdfDownload = canDownloadHandoverPdf(claims?.groups || []);
  const selectedSubstitutionId =
    searchParams.get('handoverId') || searchParams.get('hid') || '';

  // Bridge so Pickup/Dropoff forms can use the same alert API
  const pushAlert = useCallback(
    ({ type = 'info', title, message } = {}) => {
      const id = Date.now() + Math.random();

      setAlerts((prev) => [
        ...prev,
        {
          id,
          type,
          title: title || '',
          message: message || '',
        },
      ]);

      // Auto-dismiss after 3.5s
      setTimeout(() => {
        setAlerts((prev) => prev.filter((a) => a.id !== id));
      }, 3500);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        // ===== Auth / claims =====
        const { tokens } = await fetchAuthSession();
        const p = tokens?.idToken?.payload || {};
        if (!cancelled) {
          setClaims({
            email: p.email || null,
            sub: p.sub || null,
            CompanyId:
              p['custom:CompanyId'] ?? p['custom:companyId'] ?? null,
            groups: p['cognito:groups'] || [],
          });
        }

        // ===== Reservation =====
        const resResp = await client.graphql({
          query: GetReservation,
          variables: { id: reservationId },
          authMode: 'userPool',
        });
        const res = resResp?.data?.getReservation || null;
        if (!cancelled) {
          if (!res) {
            console.error('Reservation not found for handover:', reservationId);
            pushAlert({
              type: 'error',
              title: 'Reservation not found',
              message:
                'Reservation not found – cannot open handover form.',
            });
          }
          setReservation(res);
        }

        // ===== Cars =====
        const carsResp = await client.graphql({
          query: ListCars,
          authMode: 'userPool',
        });
        if (!cancelled) {
          setCars(carsResp?.data?.listCars?.items || []);
        }

        // ===== localStorage fuel for dropoff form =====
        try {
          const savedFuel = window.localStorage.getItem(
            `handover_${reservationId}_pickupFuel`
          );
          if (!cancelled && savedFuel) {
            setPickupFuelLevel(savedFuel);
          }
        } catch (e) {
          console.warn(
            'Failed to read pickup fuel from localStorage',
            e
          );
        }

        // ===== Existing handovers (pickup / dropoff) =====
        try {
          const hoResp = await client.graphql({
            query: HandoverByReservation,
            variables: { reservationId },
            authMode: 'userPool',
          });

          // Your query field is `handoverByReservationByType`
          const items =
            hoResp?.data?.handoverByReservationByType?.items || [];

          // Pick latest by createdAt per type
          const sortByCreatedDesc = (a, b) => {
            const aT = a.createdAt || '';
            const bT = b.createdAt || '';
            if (aT < bT) return 1;
            if (aT > bT) return -1;
            return 0;
          };

          const lastPickup =
            items
              .filter((h) => h.type === 'PICKUP')
              .sort(sortByCreatedDesc)[0] || null;

          const lastDropoff =
            items
              .filter((h) => h.type === 'DROPOFF')
              .sort(sortByCreatedDesc)[0] || null;
          const allSubstitutions = items
            .filter((h) => h.type === 'SUBSTITUTION')
            .sort(sortByCreatedDesc);

          if (!cancelled) {
            setPickupHandover(lastPickup);
            setDropoffHandover(lastDropoff);
            setSubstitutionHandovers(allSubstitutions);
          }
        } catch (err) {
          console.warn(
            'Failed to load pickup/dropoff handovers (optional)',
            err
          );
        }
      } catch (err) {
        console.error('Failed to load handover data', err);
        if (!cancelled) {
          pushAlert({
            type: 'error',
            title: 'Failed to load handover form',
            message: 'Check console for details.',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reservationId, pushAlert]);

  if (loading) {
    return (
      <div style={{ padding: '24px 16px', background: COLORS.gray[50], minHeight: '100vh' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ fontSize: 16, color: COLORS.gray[600], fontWeight: 500 }}>
            Loading handover form…
          </div>
        </div>
      </div>
    );
  }

  // Checklist-based lock
  const pickupSigned =
    (reservation?.pickupChecklistStatus || '').toUpperCase() === 'COMPLETED';
  const dropoffSigned =
    (reservation?.dropoffChecklistStatus || '').toUpperCase() === 'COMPLETED';

  const hasPickupHandover = !!pickupHandover;
  const hasDropoffHandover = !!dropoffHandover;

  const pickupLocked = pickupSigned || hasPickupHandover;
  const dropoffLocked = dropoffSigned || hasDropoffHandover;

  const lockedForMode =
    mode === 'pickup' ? pickupLocked : mode === 'dropoff' ? dropoffLocked : false;
  const selectedSubstitutionHandover =
    mode === 'substitution'
      ? selectedSubstitutionId
        ? substitutionHandovers.find((h) => h.id === selectedSubstitutionId) || null
        : readonlyFromQuery
          ? substitutionHandovers[0] || null
          : null
      : null;
  const substitutionSelectedReadOnly =
    mode === 'substitution' && !!selectedSubstitutionHandover && !canBypassLock;

  // FINAL READ-ONLY:
  // - ?readonly=1 forces read-only
  // - otherwise, lock if that side is signed/has a handover
  const readOnly =
    readonlyFromQuery || substitutionSelectedReadOnly || (lockedForMode && !canBypassLock);
  return (
    <>
      {/* 🔔 Toast container (bottom-right) */}
      {alerts.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {alerts.map((a) => {
            const isError = a.type === 'error';
            const isSuccess = a.type === 'success';
            const bg = isError
              ? COLORS.dangerLight
              : isSuccess
                ? COLORS.successLight
                : COLORS.gray[100];
            const color = isError
              ? '#7f1d1d'
              : isSuccess
                ? '#065f46'
                : COLORS.gray[700];

            return (
              <div
                key={a.id}
                style={{
                  padding: '12px 14px',
                  borderRadius: 10,
                  background: bg,
                  border: 'none',
                  color,
                  minWidth: 280,
                  maxWidth: 380,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)',
                  fontSize: 13,
                  fontWeight: 500,
                  animation: 'slideUp 0.3s ease-out',
                }}
              >
                {a.title && (
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {a.title}
                  </div>
                )}
                {a.message && <div style={{ lineHeight: 1.4 }}>{a.message}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ padding: '24px 16px 48px', background: COLORS.gray[50], minHeight: '100vh' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="button"
              onClick={() => navigate('/console/calendar')}
              style={{
                background: 'white',
                marginBottom: 8,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 500,
                border: `1px solid ${COLORS.gray[300]}`,
                borderRadius: 8,
              }}
            >
              ← Back to Reservations
            </Button>
          </div>

          <header style={{ marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: COLORS.gray[900] }}>
              {mode === 'pickup'
                ? 'Vehicle Pick-up Handover'
                : mode === 'dropoff'
                ? 'Vehicle Return Handover'
                : 'Vehicle Substitution Contract'}
            </h2>
            <p
              style={{
                margin: '8px 0 0',
                fontSize: 14,
                color: COLORS.gray[600],
                lineHeight: 1.5,
              }}
            >
              <code style={{ background: COLORS.gray[100], padding: '2px 6px', borderRadius: 4 }}>
                {reservationId}
              </code>
              {reservation?.reservationRef
                ? ` • Ref: ${reservation.reservationRef}`
                : ''}
            </p>
            {claims?.email && (
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 12,
                  color: COLORS.gray[500],
                }}
              >
                Staff: {claims.email}
              </p>
            )}
          </header>

          {/* Locked banner above form if there is an existing handover */}
          {mode === 'pickup' && pickupLocked && (
            <div style={{ marginBottom: 12 }}>
              <LockedHandoverNotice
                mode="pickup"
                handover={pickupHandover}
                claims={claims}
                pushAlert={pushAlert}
              />
            </div>
          )}
          {mode === 'dropoff' && dropoffLocked && (
            <div style={{ marginBottom: 12 }}>
              <LockedHandoverNotice
                mode="dropoff"
                handover={dropoffHandover}
                claims={claims}
                pushAlert={pushAlert}
              />
            </div>
          )}
          {mode === 'substitution' && substitutionHandovers.length > 0 && (
            <div
              style={{
                marginBottom: 12,
                borderRadius: 10,
                background: '#fff',
                border: `1px solid ${COLORS.gray[200]}`,
                padding: '12px 14px',
                fontSize: 13,
                color: COLORS.gray[600],
              }}
            >
              <div style={{ fontWeight: 600, color: COLORS.gray[800], marginBottom: 8 }}>
                Previous substitution contracts ({substitutionHandovers.length})
              </div>
              {substitutionHandovers.map((ho) => {
                const eq = safeParseJson(ho.equipmentNotes);
                const fromLabel = [eq.fromCarName, eq.fromCarPlate].filter(Boolean).join(' ');
                const toLabel = [
                  eq.toCarName || ho.carName,
                  eq.toCarPlate || ho.carPlate,
                ]
                  .filter(Boolean)
                  .join(' ');
                const isSelected = selectedSubstitutionHandover?.id === ho.id;

                return (
                <div
                  key={ho.id}
                  title={`${fromLabel || 'Previous car'} -> ${toLabel || 'Replacement car'}`}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '8px 0',
                    borderTop: `1px solid ${COLORS.gray[100]}`,
                  }}
                >
                  <span>
                    {ho.customerName || 'Substitution'} •{' '}
                    {ho.signedAt || ho.createdAt
                      ? new Date(ho.signedAt || ho.createdAt).toLocaleString()
                      : '—'}
                  </span>
                  <span style={{ fontSize: 12, color: COLORS.gray[500] }}>
                    {fromLabel || 'Previous car'} -&gt; {toLabel || 'Replacement car'}
                  </span>
                  <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/console/handover/${reservationId}/substitution?readonly=1&handoverId=${encodeURIComponent(
                            ho.id
                          )}`
                        )
                      }
                      style={{
                        marginTop: 0,
                        background: isSelected ? COLORS.primary : '#ffffff',
                        color: isSelected ? '#ffffff' : COLORS.gray[800],
                        border: `1px solid ${isSelected ? COLORS.primary : COLORS.gray[300]}`,
                        padding: '7px 12px',
                        fontSize: 12,
                      }}
                    >
                      {isSelected ? 'Viewing' : 'View contract'}
                    </Button>
                    {showPdfDownload && ho.id && (
                      <HandoverPdfDownloadButton
                        handoverId={ho.id}
                        label="Download PDF"
                        onError={pushAlert}
                        style={{ marginTop: 0 }}
                      />
                    )}
                  </span>
                </div>
                );
              })}
            </div>
          )}

          {mode === 'pickup' ? (
            <PickupHandoverForm
              reservationId={reservationId}
              reservation={reservation}
              cars={cars}
              claims={claims}
              location={location}
              readOnly={readOnly}
              existingHandover={pickupHandover}
              pushAlert={pushAlert}
            />
          ) : mode === 'dropoff' ? (
            <DropoffHandoverForm
              reservationId={reservationId}
              reservation={reservation}
              cars={cars}
              claims={claims}
              pickupFuelLevel={pickupFuelLevel}
              pickupHandover={pickupHandover}
              location={location}
              readOnly={readOnly}
              existingHandover={dropoffHandover}
              pushAlert={pushAlert}
            />
          ) : (
            <SubstitutionHandoverForm
              reservationId={reservationId}
              reservation={reservation}
              cars={cars}
              claims={claims}
              location={location}
              pickupHandover={pickupHandover}
              existingHandover={selectedSubstitutionHandover}
              readOnly={readOnly}
              pushAlert={pushAlert}
            />
          )}
        </div>
      </div>
    </>
  );
}
